import { defineStore } from 'pinia'
import { supabase } from '@/supabase'

// Define types for our note data and operations
interface Note {
  id: string
  content: string
  is_private: boolean
  object_type: 'list' | 'list_item' | 'review' | 'title'
  list_id?: string
  list_item_id?: string
  review_id?: string
  title_id?: string
  user_id: string
  created_at: string
  updated_at: string
}

// is_private is optional on both payloads and ignored by the notes function:
// every note is written private and readable by its author alone. The field
// stays accepted so a client running an older bundle keeps working.
interface CreateNotePayload {
  content: string
  is_private?: boolean
  object_type: 'list' | 'list_item' | 'review' | 'title'
  list_id?: string
  list_item_id?: string
  review_id?: string
  title_id?: string
}

interface UpdateNotePayload {
  content?: string
  is_private?: boolean
}

interface FetchOptions {
  limit?: number
  offset?: number
  order?: 'created_at' | 'updated_at'
  direction?: 'asc' | 'desc'
}

export const useStoreNotes = defineStore('storeNotes', {
  state: () => ({
    notes: {} as Record<string, Note>, // Object for efficient lookups by ID
    loading: false,
    error: null as string | null,
    subscription: null as any
  }),

  getters: {
    // Get a single note by ID
    getNoteById: (state) => (id: string) => {
      return state.notes[id]
    },

    // Get all notes for a specific object (list, list_item, etc.)
    getNotesByObject:
      (state) =>
      (type: 'list' | 'list_item' | 'review' | 'title', id: string) => {
        return Object.values(state.notes).filter((note) => {
          const fieldName = `${type}_id`
          return note[fieldName] === id
        })
      },

    // Get all notes belonging to a specific user
    getUserNotes: (state) => (userId: string) => {
      return Object.values(state.notes).filter(
        (note) => note.user_id === userId
      )
    },

    // Get all notes as an array
    getAllNotes: (state) => {
      return Object.values(state.notes)
    }
  },

  actions: {
    // Helper to update local state
    updateLocalNotes(notes: Note | Note[]) {
      const notesArray = Array.isArray(notes) ? notes : [notes]
      for (const note of notesArray) {
        this.notes[note.id] = note
      }
    },

    // CREATE - Edge Function call
    async createNote(payload: CreateNotePayload) {
      this.loading = true
      this.error = null

      try {
        const { data, error } = await supabase.functions.invoke('notes', {
          method: 'POST',
          body: payload
        })

        if (error) throw error

        // Add the new note to the local state
        this.updateLocalNotes(data.data)
        return data.data
      } catch (err: any) {
        this.error = err.message || 'Failed to create note'
        throw err
      } finally {
        this.loading = false
      }
    },

    // UPDATE - Edge Function call
    async updateNote(id: string, payload: UpdateNotePayload) {
      this.loading = true
      this.error = null

      try {
        const response = await supabase.functions.invoke(`notes/${id}`, {
          method: 'POST',
          body: payload
        })

        if (response.error) throw response.error

        // Correctly extract the note data from the response
        const noteData = response.data?.data

        if (!noteData) {
          throw new Error('No data returned from update operation')
        }

        // Update the note in the local state
        this.updateLocalNotes(noteData)
        return noteData
      } catch (err: any) {
        this.error = err.message || 'Failed to update note'
        throw err
      } finally {
        this.loading = false
      }
    },

    // DELETE - Edge Function call
    async deleteNote(id: string) {
      this.loading = true
      this.error = null

      try {
        const { data, error } = await supabase.functions.invoke(`notes/${id}`, {
          method: 'DELETE'
        })

        if (error) throw error

        // Remove the note from the local state
        if (this.notes[id]) {
          delete this.notes[id]
        }

        return true
      } catch (err: any) {
        this.error = err.message || 'Failed to delete note'
        throw err
      } finally {
        this.loading = false
      }
    },

    // READ/LIST - Direct Supabase queries

    // Fetch all notes with pagination and sorting options
    async fetchNotes(options: FetchOptions = {}) {
      this.loading = true
      this.error = null

      try {
        let query = supabase.from('notes').select('*')

        // Apply options
        if (options.limit) query = query.limit(options.limit)
        if (options.offset)
          query = query.range(
            options.offset,
            options.offset + (options.limit || 10) - 1
          )
        if (options.order) {
          query = query.order(options.order, {
            ascending: options.direction === 'asc'
          })
        } else {
          query = query.order('created_at', { ascending: false }) // Default order
        }

        const { data, error } = await query

        if (error) throw error

        // Update local state with fetched notes
        this.updateLocalNotes(data)
        return data
      } catch (err: any) {
        this.error = err.message || 'Failed to fetch notes'
        throw err
      } finally {
        this.loading = false
      }
    },

    // Fetch a single note by ID
    async fetchNoteById(id: string) {
      this.loading = true
      this.error = null

      try {
        const { data, error } = await supabase
          .from('notes')
          .select('*')
          .eq('id', id)
          .single()

        if (error) throw error

        // Update the local state
        this.updateLocalNotes(data)

        return data
      } catch (err: any) {
        this.error = err.message || 'Failed to fetch note'
        throw err
      } finally {
        this.loading = false
      }
    },

    // Fetch notes for a specific object type (list, list_item, etc.)
    async fetchNotesByObject(
      type: 'list' | 'list_item' | 'review' | 'title',
      id: string,
      options: FetchOptions = {}
    ) {
      this.loading = true
      this.error = null

      try {
        const fieldName = `${type}_id`
        let query = supabase.from('notes').select('*').eq(fieldName, id)

        // Apply options
        if (options.limit) query = query.limit(options.limit)
        if (options.offset)
          query = query.range(
            options.offset,
            options.offset + (options.limit || 10) - 1
          )
        if (options.order) {
          query = query.order(options.order, {
            ascending: options.direction === 'asc'
          })
        } else {
          query = query.order('created_at', { ascending: false }) // Default order
        }

        const { data, error } = await query

        if (error) throw error

        // Update local state
        this.updateLocalNotes(data)

        return data
      } catch (err: any) {
        this.error = err.message || 'Failed to fetch notes'
        throw err
      } finally {
        this.loading = false
      }
    },

    // Real-time subscriptions
    subscribeToNotes() {
      // Unsubscribe from any existing subscription
      this.unsubscribeFromNotes()

      // Create a new subscription
      this.subscription = supabase
        .channel('notes_changes')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notes'
          },
          (payload: any) => {
            // Handle different events
            if (payload.eventType === 'INSERT') {
              this.updateLocalNotes(payload.new)
            } else if (payload.eventType === 'UPDATE') {
              this.updateLocalNotes(payload.new)
            } else if (payload.eventType === 'DELETE') {
              const noteId = payload.old.id
              if (this.notes[noteId]) {
                delete this.notes[noteId]
              }
            }
          }
        )
        .subscribe()
    },

    unsubscribeFromNotes() {
      if (this.subscription) {
        supabase.removeChannel(this.subscription)
        this.subscription = null
      }
    },

    // Helper method to clear the state (useful for logout)
    clearNotes() {
      this.notes = {}
      this.error = null
      this.loading = false
      this.unsubscribeFromNotes()
    }
  }
})
